import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { FundingPolicyType } from '@shared';
import { selectClient } from '@client/model/selectors';
import { underwritingResultsKeys } from '@client/api/waterlily';
import { useTriggerUnderwriting } from './use-trigger-underwriting';
import { useRequestPolling } from './use-request-polling';

type RerunPhase = 'idle' | 'triggering' | 'polling' | 'done';

export function useRerunUnderwriting(fundingPolicyType: FundingPolicyType) {
  const queryClient = useQueryClient();
  const client = useSelector(selectClient);
  const clientId = client?.clientId;

  const {
    mutate: triggerUnderwriting,
    data: triggerData,
    isPending: isTriggering,
    statusCode,
  } = useTriggerUnderwriting();

  const [requestId, setRequestId] = useState('');
  const [phase, setPhase] = useState<RerunPhase>('idle');
  const prevTriggerData = useRef(triggerData);

  useEffect(() => {
    if (isTriggering) {
      setPhase('triggering');
    } else if (triggerData && triggerData !== prevTriggerData.current) {
      prevTriggerData.current = triggerData;
      setRequestId(triggerData);
      setPhase('polling');
    }
  }, [isTriggering, triggerData]);

  const { data: pollingData, isPolling } = useRequestPolling(requestId);

  useEffect(() => {
    if (phase !== 'polling') return;
    const status = pollingData?.status;
    if (status === 'completed') {
      setPhase('done');
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: underwritingResultsKeys.detail(clientId) });
      }
    } else if (status === 'error') {
      setPhase('done');
    }
  }, [phase, pollingData?.status, clientId, queryClient]);

  const rerun = useCallback(() => {
    triggerUnderwriting({ fundingPolicyType });
  }, [triggerUnderwriting, fundingPolicyType]);

  const isRunning = phase === 'triggering' || phase === 'polling';

  return {
    rerun,
    isRunning,
    hasCompleted: pollingData?.status === 'completed',
    hasErrored: pollingData?.status === 'error',
    statusCode,
  };
}
